import { IAssignmentTask, tGiphyImage } from 'common/modules/ReactShared';

import {
  tAssignmentConversation,
  tAssignmentConversationItem,
  tConversationCommentItem
} from 'common/modules/ReactShared/data/api/document/types';
import {
  IChatMessage,
  IChatMessageImage,
  tAssignmentNormalizedData,
  tMessageLinkPayload
} from './types';
import { getPersonInfo } from './util';

type tAssignmentAuthor = {
  avatar: string | null;
  name: string;
};

type tAssignablePayload = {
  author: tAssignmentAuthor | null;
  createdAt: string | null;
  title: string;
  text?: string | null;
  link?: {
    title: string;
    url: string;
    payload: tMessageLinkPayload;
  } | null;
  audioURL?: string | null;
  videoURL?: string | null;
  image?: IChatMessageImage | null;
  giphy?: tGiphyImage | null;
  status?: tAssignmentNormalizedData['status'];
};

interface IPrepareChatMessagesArgs {
  assignablePayload: tAssignablePayload;
  conversation: tAssignmentConversation;
}
function prepareChatMessages({
  assignablePayload,
  conversation
}: IPrepareChatMessagesArgs): IChatMessage[] {
  if (!assignablePayload) {
    return [];
  }

  const messages: Array<IChatMessage> = [];
  // First messages are from the teacher. Contains all assets, text and title
  // Message with assignment's title and text
  messages.push({
    author: assignablePayload.author,
    createdAt: assignablePayload.createdAt,
    from: 'incoming',
    key: 'task-title-and-text',
    text: assignablePayload.text,
    title: assignablePayload.title,
    link: assignablePayload.link,
    state: 'done'
  });

  // several messages with media content from assignment task
  // Audio message
  if (assignablePayload.audioURL) {
    messages.push({
      author: assignablePayload.author,
      createdAt: assignablePayload.createdAt,
      media: {
        type: 'audio',
        url: assignablePayload.audioURL
      },
      from: 'incoming',
      key: 'task-audio',
      state: 'done'
    });
  }
  // Video message
  if (assignablePayload.videoURL) {
    messages.push({
      author: assignablePayload.author,
      createdAt: assignablePayload.createdAt,
      media: {
        type: 'video',
        url: assignablePayload.videoURL
      },
      from: 'incoming',
      key: 'task-video',
      state: 'done'
    });
  }
  // Image message
  if (assignablePayload.image?.imageURL || assignablePayload.image?.mobileImageURL) {
    messages.push({
      author: assignablePayload.author,
      createdAt: assignablePayload.createdAt,
      media: {
        type: 'image',
        payload: assignablePayload.image
      },
      from: 'incoming',
      key: 'task-image',
      state: 'done'
    });
  }

  // Giphy message
  if (assignablePayload.giphy) {
    messages.push({
      author: assignablePayload.author,
      createdAt: assignablePayload.createdAt,
      media: {
        type: 'giphy',
        payload: assignablePayload.giphy
      },
      from: 'incoming',
      key: 'task-giphy',
      state: 'done'
    });
  }

  // User answers and teacher comments
  const _conversationMessages = conversation
    .map((conversationItem): IChatMessage => {
      const { audioURL, type, createdAt, content } = conversationItem;
      const isComment = isConversationComment(conversationItem);
      const messageAuthor = getConversationItemAuthor(conversationItem);
      let from: IChatMessage['from'] = isComment ? 'incoming' : 'outgoing';

      if (isComment && isBotMessage(conversationItem)) {
        from = 'incoming:ai';
      }

      return {
        key: `conversation-${type}-${createdAt}`,
        author: messageAuthor,
        media: audioURL
          ? {
              type: 'audio',
              url: audioURL
            }
          : null,
        file: conversationItem.file
          ? {
              metadata: conversationItem.file.metadata,
              url: conversationItem.file.url
            }
          : null,
        text: content,
        createdAt,
        from,
        state: 'done'
      };
    })
    .sort((first, second) => {
      const firstTime = first.createdAt ? new Date(first.createdAt).getTime() : 0;
      const secondTime = second.createdAt ? new Date(second.createdAt).getTime() : 0;
      return firstTime - secondTime;
    });

  // Mark last message as editable if it's an outgoing message
  const lastMessage = _conversationMessages
    ? _conversationMessages[_conversationMessages.length - 1]
    : undefined;
  // .reverse()
  // .find((message) => message.from === 'outgoing');
  if (
    lastMessage &&
    assignablePayload.status === 'pending' &&
    lastMessage.from === 'outgoing'
  ) {
    lastMessage.editable = true;
  }

  return [...messages, ..._conversationMessages];
}

function prepareChatMessagesFromNormalizedAssignmentData({
  status,
  conversation,
  assignmentTask
}: tAssignmentNormalizedData): Array<IChatMessage> {
  return prepareChatMessages({
    assignablePayload: {
      author: {
        avatar: assignmentTask.author.brand ?? null,
        name: assignmentTask.author.name
      },
      createdAt: null,
      title: assignmentTask.title,
      text: assignmentTask.text,
      link: assignmentTask.lesson
        ? {
            title: assignmentTask.lesson.title,
            url: `/lessons/${assignmentTask.lesson.id}`,
            payload: {
              type: 'lessonId',
              data: assignmentTask.lesson.id
            }
          }
        : null,
      audioURL: assignmentTask.audioURL,
      videoURL: assignmentTask.videoURL,
      status,
      image: {
        imageURL: assignmentTask.imageURL,
        mobileImageURL: assignmentTask.mobileImageURL,
        coverImg: assignmentTask.coverImg,
        imageAlt: assignmentTask.imageAlt
      },
      giphy: assignmentTask.giphyImage
    },
    // comments,
    conversation
  });
}

type tPrepareTaskMessagesArg = {
  task: IAssignmentTask;
  assignment: {
    assignable: tAssignmentNormalizedData['assignmentTask'];
    conversation: tAssignmentNormalizedData['conversation'];
    status: tAssignmentNormalizedData['status'];
  } | null;
};
function prepareChatMessagesForTaskAssignment({
  task,
  assignment
}: tPrepareTaskMessagesArg): Array<IChatMessage> {
  let assignmentAuthor: tAssignmentAuthor | null = null;

  if (assignment?.assignable.author) {
    assignmentAuthor = {
      name: assignment.assignable.author.name,
      avatar: assignment.assignable.author.brand ?? null
    };
  } else if (task.reviewer.name) {
    assignmentAuthor = {
      name: task.reviewer.name,
      avatar: task.reviewer.imageURL ?? null
    };
  }

  return prepareChatMessages({
    assignablePayload: {
      author: assignmentAuthor,
      createdAt: null,
      title: task.title,
      text: task.introduction,
      link: null,
      status: assignment?.status,
      audioURL: task.audioURL,
      videoURL: task.videoURL,
      image: {
        imageURL: task.imageURL,
        mobileImageURL: task.mobileImageURL,
        coverImg: task.coverImg,
        imageAlt: task.imageAlt
      },
      giphy: task.giphyImage
    },
    conversation: assignment?.conversation ?? []
  });
}

export {
  prepareChatMessagesFromNormalizedAssignmentData,
  prepareChatMessagesForTaskAssignment
};

function isConversationComment(
  item: tAssignmentConversationItem
): item is tConversationCommentItem {
  return (item as tConversationCommentItem).type === 'Comment';
}

function isBotMessage(item: tConversationCommentItem) {
  return !!item.author.system;
}

function getConversationItemAuthor(
  item: tAssignmentConversationItem
): IChatMessage['author'] {
  let author: IChatMessage['author'] = null;
  if (isConversationComment(item)) {
    author = getPersonInfo(item.author);
  } else {
    author = null;
  }

  return author;
}
